import { useEffect, useRef } from "react";
import { runtimeConfig } from "../../config/runtime";
import { fetchScreenData, precacheTimetableData } from "../../services/api";
import { prefetchScreenAssets } from "../../utils/assetPrefetcher";
import { azanVoice } from "./azanVoices";
import { HomeSettings, azanSettingsFor } from "./homeSettings";

// Every Home code, so turning the tablet or switching the screen style works offline.
const HOME_CODES = [
  "home",
  "home-portrait",
  "home-simple",
  "home-portrait-simple",
  "home-next",
  "home-portrait-next",
];
const ONLINE_GAP_MS = 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

// Images and the azan are kept by the service worker as they pass through it. On a tablet's
// first visit it only takes over after the page has started, so wait for it.
const serviceWorkerInControl = (): Promise<void> =>
  new Promise((resolve) => {
    if (!("serviceWorker" in navigator) || navigator.serviceWorker.controller) return resolve();
    navigator.serviceWorker.addEventListener("controllerchange", () => resolve(), { once: true });
  });

const saveOfflineCopies = async (settings: HomeSettings) => {
  // Kept even while alerts are off, so turning the azan on during an outage still works.
  await fetch(azanVoice(azanSettingsFor(settings).voice).file).catch(() => {});
  for (const masjid of settings.favourites) {
    const picked = settings.masjidScreens?.[masjid.id];
    for (const code of picked ? [...HOME_CODES, `home-s${picked}`] : HOME_CODES) {
      const screen = await fetchScreenData(masjid.id, code).catch(() => null);
      if (screen) await prefetchScreenAssets(screen).catch(() => {});
    }
    await fetch(`${runtimeConfig.storageUrl}/masaajidimages/${masjid.id}.png`, {
      mode: "no-cors",
    }).catch(() => {});
    await precacheTimetableData(masjid.id).catch(() => {});
  }
};

// Keeps every favourite ready for a power cut or a dead router: its screens, posters, photo,
// this month and next, plus the azan recording. Only live audio and video need the internet.
export const useOfflineCopies = (settings: HomeSettings | null) => {
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const lastRun = useRef(0);
  const favourites = settings?.favourites.map((m) => m.id).join(",");
  const voice = settings ? azanSettingsFor(settings).voice : undefined;

  useEffect(() => {
    if (!favourites) return;
    const run = async (minGapMs: number) => {
      if (!navigator.onLine || Date.now() - lastRun.current < minGapMs) return;
      lastRun.current = Date.now();
      await serviceWorkerInControl();
      if (settingsRef.current) await saveOfflineCopies(settingsRef.current);
    };
    run(0);
    const onOnline = () => run(ONLINE_GAP_MS);
    const daily = window.setInterval(() => run(0), DAY_MS);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.clearInterval(daily);
    };
  }, [favourites, voice]);
};
